import { DollarOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row, Space, Table, Tag } from 'antd';
import { useForm } from 'antd/es/form/Form';
import Popconfirm from 'antd/lib/popconfirm';
import { ColumnsType } from 'antd/lib/table';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { selectUser } from '../../../auth/states/userSlice';
import { IAllInvoices } from '../../../common/types/commonInterfaces';
import { IPermission } from '../../../common/types/commonTypes';
import {
  FormatDate,
  debounce,
  formatAmount,
  separateClient,
} from '../../../common/utils/common.utils';
import usePagination from '../../../common/utils/usePagination';
import useUrlParams from '../../../common/utils/useUrlParams';
import BreadCrumb from '../../../components/common/breadCrumb/BreadCrumb';
import { DateInput, FormInput } from '../../../components/common/FormItem/FormItems';
import Reload from '../../../components/common/reload/Reload';
import { loadingIndicator } from '../../../components/common/spinner/LoadingIndicator';
import { formatDateRangeArray, parseDateRangeObject } from '../../../helpers/healper';
import InvoiceOtherAddMoneyModal from '../../Invoice(Other)/Modals/InvoiceOtherAddMoneyModal';
import {
  useDeleteInvoiceTourMutation,
  useGetAllInvoiceTourV2Query,
} from '../Endpoints/GenerateInvoiceEndpoints';
import AddCostingFormModal from '../Modals/AddCostingFormModal';
type Props = {
  permission: IPermission | undefined;
  addMRPermission: ['*'] | undefined;
};
const AllInvoiceTours = ({ permission, addMRPermission }: Props) => {
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };
  const [columnIndex, setColumnIndex] = useState<number | null>(null);

  const {
    data,
    isFetching,
    refetch,
    isLoading: dataIsLoading,
  } = useGetAllInvoiceTourV2Query({
    ...getAllUrlParams(),
    page,
    size,
  });

  const [isTrash, setIsTrash] = useState(false);
  const user = useAppSelector(selectUser);
  const navigate = useNavigate();

  const [deleteTour, { isSuccess, isLoading: deleteLoading, isError }] =
    useDeleteInvoiceTourMutation();

  useEffect(() => {
    if (isSuccess) {
      setColumnIndex(null);
    } else if (isError) {
      setColumnIndex(null);
    }
  }, [isSuccess, isError]);
  const handleClientDelete = async (values: IAllInvoices, index: number) => {
    const body = {
      tour_invoice_id: values.invoice_id,
      invoice_has_deleted_by: user?.user_id as number,
    };
    setColumnIndex(index);
    await deleteTour(body);
  };

  //column
  const location = useLocation();
  const column: ColumnsType<IAllInvoices> = [
    {
      title: 'SL.',
      render: (_, data, index) => (
        <>{((pagination?.current || 1) - 1) * (pagination?.pageSize || 100) + 1 + index}</>
      ),
    },
    {
      title: 'Invoice No.',
      dataIndex: 'invoice_no',
      key: 'invoice_no',
      width: 120,
    },
    {
      title: 'Sales Dates',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      render: (_, data) => <>{FormatDate(data.invoice_date)}</>,
      width: 135,
    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (value, record) => {
        const { client_id, combined_id } = separateClient(record?.comb_client);

        return (
          <Link
            to={
              client_id
                ? `/clients/details/${record?.comb_client}`
                : `/combineClients/details/${combined_id}`
            }
          >
            <span>
              {record.client_name}
              {record.mobile ? ' ' + record.mobile : ''}
            </span>
          </Link>
        );
      },
    },
    {
      title: 'Sales Price',
      dataIndex: 'net_total',
      key: 'net_toal',
      width: 120,
      align: 'right',
      sorter: (a, b) => Number(a.net_total) - Number(b.net_total),

      render: (curr) => <span className='color-sales'>{formatAmount(curr)}</span>,
    },
    {
      title: 'Rec Amount',
      dataIndex: 'invclientpayment_amount',
      key: 'invclientpayment_amount',
      align: 'right',
      width: 120,
      render: (curr) => <span className='color-collection'>{formatAmount(curr)}</span>,
    },
    {
      title: 'Due Amount',
      align: 'right',
      width: 120,
      render(_, record) {
        const dueAmount = Number(record?.net_total) - Number(record?.invclientpayment_amount);

        return dueAmount > 0 ? (
          <span style={{ color: 'red' }}>
            {formatAmount(Number(record?.net_total) - Number(record?.invclientpayment_amount))}
          </span>
        ) : (
          <Tag color='cyan' icon={<DollarOutlined />}>
            PAID
          </Tag>
        );
      },
      sorter: (a, b) => Number(a.net_total) - Number(b.net_total),
    },
    {
      title: 'MR. No.',
      dataIndex: 'money_receipt_num',
      key: 'money_receipt_num',
      width: 120,
      render: (_, data) => (
        <span>
          {data?.money_receipt_num?.split(',').map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </span>
      ),
    },
    {
      title: 'Sales by',
      dataIndex: 'sales_by',
      key: 'sales_by',
      width: 150,
    },
    {
      title: 'Action',
      key: 'operation',
      width: 400,
      render: (_, data, index) => (
        <Space size='small'>
          {permission?.['read:any'] && !isTrash && (
            <Link state={location.pathname} to={`/invoiceTour/details/${data.invoice_id}`}>
              <Button size='small' type='primary'>
                View
              </Button>
            </Link>
          )}

          {permission?.['update:any'] && !isTrash && (
            <Button
              size='small'
              type='primary'
              onClick={() => {
                navigate(`/tour/invoice/edit/${data.invoice_id}`, {
                  state: location.pathname,
                });
              }}
            >
              Edit
            </Button>
          )}

          {permission?.['delete:any'] &&
            (Number(data?.invclientpayment_amount) <= 0 ? (
              <Popconfirm title='Sure to delete?' onConfirm={() => handleClientDelete(data, index)}>
                <Button danger size='small' type='primary' loading={columnIndex === index && true}>
                  Delete
                </Button>
              </Popconfirm>
            ) : (
              <Button disabled size='small'>
                Delete
              </Button>
            ))}

          <AddCostingFormModal invoiceId={data?.invoice_id} />

          {addMRPermission &&
            (Number(data?.net_total || 0) !== Number(data?.invclientpayment_amount || 0) ? (
              <InvoiceOtherAddMoneyModal
                disabled={data?.net_total === data?.invclientpayment_amount}
                invoice_id={data?.invoice_id}
                InvocieModalTitle={'Invoice Tour'}
              />
            ) : (
              <Button disabled size='small'>
                Money Receipt
              </Button>
            ))}
        </Space>
      ),
    },
  ];

  const dataWithKey = data?.data?.map((item, index) => ({
    key: String(index),
    ...item,
  }));
  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 500),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search,
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);

  return (
    <div>
      <BreadCrumb arrOfOption={['Invoice Tour', 'Lists']} />
      {/* HEADER */}
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 10 }} justify={'space-between'}>
        <Col span={12}>
          <Row gutter={10}>
            {permission?.['create:any'] && (
              <Col xs={24} sm={12} md={8} lg={8}>
                <Link to={'/tour/add'}>
                  <Button type='primary' icon={<PlusOutlined />} style={{ width: '100%' }}>
                    Create
                  </Button>
                </Link>
              </Col>
            )}

            <Col xs={24} sm={12} md={8} lg={8} style={{ display: 'flex' }}>
              <Col>
                <Reload
                  refetch={() => {
                    refetch();
                  }}
                />{' '}
              </Col>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <DateInput
                name={'date_range'}
                rangePicker
                size={8}
                noStyle
                onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
              />
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
            </Row>
          </Form>
        </Col>
      </Row>

      {/* table */}

      <div>
        <span style={{ float: 'left', marginBottom: '1rem' }}>
          You have total <strong>{data?.count ? data?.count : data?.data?.length}</strong> rows
        </span>
        <Table
          rowClassName={(record) => {
            const { invoice_is_reissued: reissue, invoice_is_refund: refund } = record;
            let className = '';

            if (reissue) return (className = 'reissued-row-style');
            if (refund) return (className = 'refund-row-style');

            return className;
          }}
          size='small'
          bordered
          rowKey={(e) => e.invoice_id}
          columns={column}
          loading={{
            spinning: dataIsLoading || isFetching,
            indicator: loadingIndicator,
          }}
          dataSource={dataWithKey}
          scroll={{ x: 'max-content' }}
          pagination={changePagination(data?.count)}
        />
      </div>
    </div>
  );
};

export default AllInvoiceTours;
